'use client'

import * as React from 'react'
import {
  Command as CommandPrimitive,
  useCommandState,
  defaultFilter as defaultCommandFilter,
} from 'cmdk'

import { CheckIcon, SearchIcon } from 'lucide-react'
import { cn, tv } from '@joyful/utils'
import type { DropdownMenuItemProps } from './DropdownMenu'
import { Ellipsis } from './Ellipsis'
import { inputVariants } from './Input'
import { itemVariants } from './Item'
import { Separator } from './Separator'
import { Slot } from './Slot'
import type { VariantProps } from '@joyful/utils'
import type {
  ComponentProps,
  ComponentRef,
  ReactElement,
  ReactNode,
} from 'react'

export const commandVariants = tv({
  base: 'bg-tremor-background text-tremor-content-strong flex size-full min-h-0 flex-col overflow-hidden rounded-md',

  variants: {
    gap: {
      md: 'gap-3',
    },
    height: {
      auto: 'h-auto',
    },
    variant: {
      menu: 'w-96 rounded-md border shadow-md',
      plain: 'bg-transparent',
    },
    width: {
      full: 'max-w-full',
      md: 'max-w-md',
      sm: 'max-w-xs',
    },
  },
})

export type CommandProps = Omit<
  ComponentProps<typeof CommandPrimitive>,
  'className'
> &
  VariantProps<typeof commandVariants>
export type CommandRef = ComponentRef<typeof CommandPrimitive>

export { CommandPrimitive }

export const commandItemScopeSeparator = '\u001F'

const CommandGroupScopeContext = React.createContext<string | null>(null)

function filterIgnoringItemScope(
  value: string,
  search: string,
  keywords?: Array<string>,
) {
  const [searchableValue = ''] = value.split(commandItemScopeSeparator)
  return defaultCommandFilter(searchableValue, search, keywords)
}

export function Command({
  filter = filterIgnoringItemScope,
  gap,
  height,
  variant,
  width,
  ...props
}: CommandProps) {
  return (
    <CommandPrimitive
      filter={filter}
      className={commandVariants({ gap, height, variant, width })}
      {...props}
    />
  )
}

export type CommandHeaderContentProps = Omit<ComponentProps<'div'>, 'className'>

export function CommandHeaderContent({
  children,
  ...props
}: CommandHeaderContentProps) {
  return (
    <div className="flex min-w-0 flex-[1_0_0] flex-col" {...props}>
      {children}
    </div>
  )
}

export const commandHeaderVariants = tv({
  base: 'flex min-h-12 items-center gap-x-2 border-b',

  defaultVariants: {
    padding: 'default',
  },

  variants: {
    justify: {
      between: 'justify-between',
    },
    padding: {
      default: 'px-2 py-2',
      md: 'p-3',
    },
  },
})

export interface CommandHeaderProps
  extends
    Omit<ComponentProps<'header'>, 'className'>,
    VariantProps<typeof commandHeaderVariants> {
  endAdornment?: ReactNode
  startAdornment?: ReactNode
}

export function CommandHeader({
  children,
  endAdornment,
  justify,
  padding,
  startAdornment,
  ...props
}: CommandHeaderProps) {
  return (
    <header className={commandHeaderVariants({ justify, padding })} {...props}>
      {startAdornment}
      {typeof children === 'string' ? (
        <CommandHeaderContent>
          <span className="text-tremor-content text-sm font-medium">
            {children}
          </span>
        </CommandHeaderContent>
      ) : (
        children
      )}
      {endAdornment}
    </header>
  )
}

export type CommandFooterProps = Omit<ComponentProps<'footer'>, 'className'>

export function CommandFooter(props: CommandFooterProps) {
  return <footer className="border-t p-2" {...props} />
}

export const commandInputVariants = tv({
  variants: {
    position: {
      sticky: 'bg-tremor-background sticky top-0 z-20',
    },
  },
})

export interface CommandInputProps
  extends
    Omit<ComponentProps<typeof CommandPrimitive.Input>, 'className' | 'size'>,
    Pick<
      VariantProps<typeof inputVariants>,
      'focusVariant' | 'roundingVariant' | 'size'
    >,
    VariantProps<typeof commandInputVariants> {
  endAdornment?: ReactNode
  startAdornment?: ReactNode

  /**
   * @default <SearchIcon />
   */
  startIcon?: ReactElement<unknown> | null
}

const defaultStartIcon = <SearchIcon />

export const CommandPrimitiveInput = CommandPrimitive.Input

export function CommandInput({
  endAdornment,
  focusVariant = 'none',
  position,
  roundingVariant = 'top',
  size,
  startAdornment,
  startIcon = defaultStartIcon,
  ...props
}: CommandInputProps) {
  const classNames = inputVariants({
    focusVariant,
    roundingVariant,
    size,
  })

  return (
    <div
      className={cn(
        classNames.base(),
        'border-b',
        startAdornment ? 'pr-3 pl-2' : 'px-3',
        commandInputVariants({ position }),
      )}
      cmdk-input-wrapper=""
    >
      {startAdornment}
      {startAdornment ? <Separator variant="vertical" /> : null}
      {startIcon ? (
        <Slot className={cn(classNames.icon(), 'opacity-50')}>{startIcon}</Slot>
      ) : null}
      <CommandPrimitiveInput className={classNames.input()} {...props} />
      {endAdornment}
    </div>
  )
}

export const commandListVariants = tv({
  base: 'scrollable-y min-h-0 focus:outline-hidden',

  defaultVariants: {
    maxHeight: 'default',
  },

  variants: {
    fade: {
      input: 'scroll-fade-offset-t-9.75',
    },
    maxHeight: {
      '2xl': 'max-h-160',
      default: 'max-h-80',
      lg: 'max-h-108',
      md: 'max-h-72',
      none: 'max-h-none',
      popover: 'max-h-(--radix-popover-content-available-height)',
      sm: 'max-h-64',
      viewport: 'max-h-128',
      xl: 'max-h-112',
      xs: 'max-h-48',
    },
  },
})

export type CommandListProps = Omit<
  ComponentProps<typeof CommandPrimitive.List>,
  'className'
> &
  VariantProps<typeof commandListVariants>

export function CommandList({ fade, maxHeight, ...props }: CommandListProps) {
  return (
    <CommandPrimitive.List
      className={commandListVariants({ fade, maxHeight })}
      {...props}
    />
  )
}

export const commandEmptyVariants = tv({
  base: 'text-tremor-content text-center',

  defaultVariants: {
    padding: 'default',
    size: 'sm',
  },

  variants: {
    padding: {
      compact: 'p-2.5',
      default: 'py-6',
    },
    size: {
      sm: 'text-sm',
      xs: 'text-xs',
    },
  },
})

export type CommandEmptyProps = Omit<
  ComponentProps<typeof CommandPrimitive.Empty>,
  'className'
> &
  VariantProps<typeof commandEmptyVariants>

export function CommandEmpty({ padding, size, ...props }: CommandEmptyProps) {
  return (
    <CommandPrimitive.Empty
      className={commandEmptyVariants({ padding, size })}
      {...props}
    />
  )
}

export type CommandLoadingProps = Omit<
  ComponentProps<typeof CommandPrimitive.Loading>,
  'className'
>

export function CommandLoading(props: CommandLoadingProps) {
  return (
    <CommandPrimitive.Loading
      className="text-tremor-content py-6 text-center text-sm"
      {...props}
    />
  )
}

export type CommandGroupProps = Omit<
  ComponentProps<typeof CommandPrimitive.Group>,
  'className'
>

export function CommandGroup(props: CommandGroupProps) {
  const groupScope = React.useId()

  return (
    <CommandGroupScopeContext value={groupScope}>
      <CommandPrimitive.Group
        className="text-foreground **:[[cmdk-group-heading]]:text-tremor-content overflow-hidden p-1 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium"
        {...props}
      />
    </CommandGroupScopeContext>
  )
}

export const commandSeparatorVariants = tv({
  base: 'bg-tremor-border -mx-1 h-px',

  defaultVariants: {
    spacing: 'default',
  },

  variants: {
    spacing: {
      default: 'my-1',
      flush: 'my-0',
    },
  },
})

export type CommandSeparatorProps = Omit<
  ComponentProps<typeof CommandPrimitive.Separator>,
  'className'
> &
  VariantProps<typeof commandSeparatorVariants>

export function CommandSeparator({ spacing, ...props }: CommandSeparatorProps) {
  return (
    <CommandPrimitive.Separator
      className={commandSeparatorVariants({ spacing })}
      {...props}
    />
  )
}

export const commandItemVariants = tv({
  base: 'group',

  variants: {
    justify: {
      center: 'justify-center',
    },
    tone: {
      brand: 'text-tremor-brand font-medium',
      subtle: 'text-tremor-content-subtle',
    },
  },
})

export interface CommandItemProps
  extends
    Omit<ComponentProps<typeof CommandPrimitive.Item>, 'className'>,
    Pick<DropdownMenuItemProps, 'align' | 'animation' | 'size' | 'subtitle'>,
    VariantProps<typeof commandItemVariants> {
  endAdornment?: ReactElement<unknown>
  /**
   * @default false
   */
  isSelected?: boolean

  startAdornment?: ReactNode
  startIcon?: ReactElement
}

export function CommandItem({
  align,
  animation,
  children,
  endAdornment,
  isSelected,
  justify,
  size,
  startAdornment,
  startIcon,
  subtitle,
  tone,
  ...props
}: CommandItemProps) {
  const groupScope = React.use(CommandGroupScopeContext)
  const classNames = itemVariants({
    align,
    animation,
    isInteractive: true,
    size,
  })
  const itemValue =
    props.value ?? (typeof children === 'string' ? children : undefined)
  const scopedItemValue =
    groupScope !== null && itemValue !== undefined
      ? `${itemValue}${commandItemScopeSeparator}${groupScope}`
      : itemValue

  return (
    <CommandPrimitive.Item
      className={cn(classNames.base(), commandItemVariants({ justify, tone }))}
      {...props}
      value={scopedItemValue}
    >
      {startAdornment ? (
        <span className="mr-2 flex size-5 shrink-0 items-center justify-center text-xl">
          {startAdornment}
        </span>
      ) : startIcon ? (
        <Slot className={classNames.icon()}>{startIcon}</Slot>
      ) : null}

      <div className={cn(classNames.container(), 'gap-0.5')}>
        {children}
        {subtitle === undefined ? null : typeof subtitle === 'string' ? (
          <Ellipsis asChild numLines={2}>
            <p className={classNames.subtitle()}>{subtitle}</p>
          </Ellipsis>
        ) : (
          subtitle
        )}
      </div>

      {endAdornment ? (
        <span className="ml-2 shrink-0">{endAdornment}</span>
      ) : null}

      {typeof isSelected === 'boolean' ? (
        <CheckIcon
          className={cn(
            'size-4 shrink-0 stroke-2',
            isSelected ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : null}
    </CommandPrimitive.Item>
  )
}

export { defaultCommandFilter, useCommandState }
